import { rgba } from 'polished'
import {
  COLOR_BASE_DARK_NAVY,
  COLOR_BASE_NAVY,
  COLOR_BASE_LIGHT_NAVY,
  COLOR_BASE_WHITE,
  COLOR_BASE_GRAY,
  COLOR_BASE_BLACK,
  COLOR_SEMANTIC_ERROR,
  COLOR_SEMANTIC_ERROR_LIGHT,
  COLOR_SEMANTIC_ERROR_BACK,
  COLOR_SEMANTIC_SUCCESS,
  COLOR_SEMANTIC_SUCCESS_LIGHT,
  COLOR_SEMANTIC_WARNING,
  COLOR_SEMANTIC_INFO,
  COLOR_BRAND_PRIMARY_HOVER,
  COLOR_BRAND_PRIMARY_ACTIVE,
  COLOR_UI_BACKGROUND,
  COLOR_UI_SURFACE,
  COLOR_UI_BORDER,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_DISABLED,
  RADIUS_SM,
  SHADOW_SM,
  BREAKPOINT_XS,
  BREAKPOINT_SM,
  BREAKPOINT_MD,
  BREAKPOINT_LG,
  BREAKPOINT_XL,
} from './generated-theme-values'

export const headerHeight = '56px'

export interface Sizes {
  XXS: number | string
  XS: number | string
  S: number | string
  M: number | string
  L: number | string
  XL: number | string
  XXL: number | string
}

export const gapSizes: Partial<Sizes> = {
  S: '10px',
  M: '20px',
  L: '30px',
  XL: '40px',
  XXL: '50px',
}

export const screenSizes: Partial<Sizes> = {
  XS: BREAKPOINT_XS,
  S: BREAKPOINT_SM,
  M: BREAKPOINT_MD,
  L: BREAKPOINT_LG,
  XL: BREAKPOINT_XL,
}

export const fontSizes: Sizes = {
  XXS: '14px',
  XS: '16px',
  S: '18px',
  M: '24px',
  L: '32px',
  XL: '36px',
  XXL: '48px',
}

export const lineHeights: Sizes = {
  XXS: '14px',
  XS: '24px',
  S: '18px',
  M: '24px',
  L: '32px',
  XL: '36px',
  XXL: '48px',
}

export const textAreaSizes = {
  S: '92px',
  M: '108px',
  L: '128px',
}

interface ThemeWithStates {
  [propName: string]: string
}

export interface Colors {
  darkNavy: string
  navy: string
  lightNavy: string
  white: string
  gray: string
  error: string
  bannerError: string
  black: string
  green: string
  blueLight: string
  blueMed: string
  blueDark: string
  bannerSuccess: string
  grayDivider: string
  tableHighlight: string
  actionInfo: string
  actionDiagram: string
  dropBack: string
  dropBorder: string
  warning: string
  progressBack: string
  slateGrey: string
  subRow: string
  errorBack: string
  errorRed: string
  lightGrey: string
  darkGrey: string
  fadedGrey: string
}

export const colors: Colors = {
  darkNavy: COLOR_BASE_DARK_NAVY,
  navy: COLOR_BASE_NAVY,
  lightNavy: COLOR_BASE_LIGHT_NAVY,
  white: COLOR_BASE_WHITE,
  gray: COLOR_BASE_GRAY,
  black: COLOR_BASE_BLACK,
  error: COLOR_SEMANTIC_ERROR,
  green: COLOR_SEMANTIC_SUCCESS,
  blueDark: '#2B3B44',
  blueMed: '#2C3E50',
  blueLight: COLOR_SEMANTIC_INFO,
  bannerError: '#FFC6CC',
  bannerSuccess: COLOR_SEMANTIC_SUCCESS_LIGHT,
  grayDivider: COLOR_UI_BORDER,
  tableHighlight: rgba('#FFF', 0.1),
  actionInfo: '#4e6370',
  actionDiagram: '#334652',
  dropBack: rgba('#000', 0.05),
  dropBorder: rgba('#000', 0.25),
  warning: COLOR_SEMANTIC_WARNING,
  progressBack: '#1D202B',
  slateGrey: rgba('#FFF', 0.25),
  subRow: '#374a57',
  errorBack: COLOR_SEMANTIC_ERROR_BACK,
  errorRed: COLOR_SEMANTIC_ERROR_LIGHT,
  lightGrey: '#D9D9D9',
  darkGrey: '#959B9E',
  fadedGrey: rgba(191, 191, 191, 0.4),
}

export interface Theme {
  [propName: string]: string | number | ThemeWithStates | { [propName: string]: ThemeWithStates } | undefined
  selected?: Themes
  primary: string
  secondary: string
  gray: string
  action: string
  error: string
  boxShadow: string
  white: string
  black: string
  radius: string
  tertiary: string
  chart: string
  header: string
  title: string
  beforeSelected: string
  afterSelected: string
  panelBack: string
  secondaryPanel: string
  subRow: string
  actionsDrop: ThemeWithStates
  tableRow: string
  invertedButton: string
  label: string
  inputBorder: string
  selectorBack: string
  breadcrumb: string
  progressBack: string
  grayDivider: string
  actionInfo: string
  oddRow: string
  scrollBar: string
  disabled: string
  welcomeBack: string
  whiteIcon: string
  boxHover: string
  listText: string
  listDesc: string
  listBack: string
  listCard: string
  listCompleted: string
  listIcon: string
  listBorder: string
  whiteGlove: string
  buttonHover: string
  buttonActive: string
  checkbox: string
  warning: string
  newNav: string
  tableHeader: string
  rowHighlight: string
  inputBack: string
  buttonBack: string
  warningInverted: string
  input: ThemeWithStates
  dock: ThemeWithStates
  tab: ThemeWithStates
  drop: ThemeWithStates
  profileDrop: string
  ellipseOption: string
  modalBack: string
  modalOptionsBack: string
  chartBack: string
  walletBack: string
  walletCat: string
  button: { [propName: string]: ThemeWithStates }
}

export const equaTheme: Theme = {
  primary: rgba('#FFF', 0.05),
  secondary: colors.lightNavy,
  accent: colors.green,
  action: colors.blueDark,
  error: colors.error,
  boxShadow: SHADOW_SM,
  highlight: rgba('#FFF', 0.05),
  gray: rgba('#FFF', 0.5),
  white: colors.white,
  black: colors.black,
  radius: RADIUS_SM,
  tertiary: colors.blueMed,
  chart: colors.blueLight,
  header: COLOR_UI_BACKGROUND,
  title: colors.white,
  beforeSelected: COLOR_TEXT_SECONDARY,
  afterSelected: rgba('#FFF', 0.5),
  panelBack: rgba('#FFF', 0.05),
  secondaryPanel: COLOR_UI_SURFACE,
  subRow: colors.subRow,
  actionsDrop: { default: '#435763', hover: '#476171' },
  tableRow: rgba('#FFF', 0.05),
  invertedButton: colors.blueDark,
  label: colors.white,
  inputBorder: colors.white,
  selectorBack: colors.tableHighlight,
  breadcrumb: '#9CA6AD',
  progressBack: colors.progressBack,
  grayDivider: colors.grayDivider,
  actionInfo: colors.actionInfo,
  oddRow: rgba('#FFF', 0.05),
  scrollBar: colors.blueMed,
  disabled: colors.fadedGrey,
  welcomeBack: COLOR_UI_BACKGROUND,
  whiteIcon: colors.white,
  boxHover: '#416171',
  listText: colors.white,
  listDesc: '#9CA6AD',
  listBack: '#263946',
  listCard: colors.lightNavy,
  listCompleted: '#263946',
  listIcon: colors.white,
  listBorder: rgba('#CEDAE1', 0.3),
  whiteGlove: colors.grayDivider,
  buttonHover: '#476171',
  dropHover: '#30424D',
  buttonActive: rgba('#30424D', 0.6),
  checkbox: '#2B3D47',
  warning: '#F5A623',
  newNav: '#435763',
  tableHeader: '#283840',
  rowHighlight: '#283840',
  inputBack: '#30424D',
  buttonBack: colors.grayDivider,
  warningInverted: '#283840',
  input: { default: '#30424D', basic: '#2B3D47', disabled: rgba('#516F77', 0.1) },
  dock: { default: '#394E5B', hover: '#2B3D47', disabled: rgba('#516F77', 0.1) },
  tab: { default: '#394E5B', hover: '#30424D', disabled: rgba('#516F77', 0.1) },
  drop: { options: '#1F313A', back: '#1F313A', text: '#FFFFFF', selected: '#262626' },
  profileDrop: '#30424D',
  ellipseOption: '#1F313A',
  modalBack: '#1F313A',
  modalOptionsBack: '#30424D',
  chartBack: colors.blueDark,
  walletBack: '#30424D',
  walletCat: '#3B515E',
  button: {
    primary: { default: colors.green, hover: COLOR_BRAND_PRIMARY_HOVER, submitting: COLOR_BRAND_PRIMARY_ACTIVE, disabled: rgba('#33BB40', 0.32) },
    inverted: { default: '#283840', hover: '#2B3D47', submitting: COLOR_UI_SURFACE, hoverColor: COLOR_BRAND_PRIMARY_HOVER, disabled: rgba(81, 111, 119, 0.101961) },
    cancel: { default: COLOR_UI_BORDER, hover: '#476171', submitting: COLOR_UI_SURFACE, disabled: rgba('#516F77', 0.1) },
    error: { default: colors.errorRed, hover: '#CC4545', disabled: rgba('#516F77', 0.1) },
    warning: { default: COLOR_SEMANTIC_WARNING, hover: '#F17C15', submitting: '#C35B00', text: '#262626', disabled: rgba('#516F77', 0.1) },
    warningI: { default: '#283840', hover: '#2B3D47', submitting: COLOR_UI_SURFACE, disabled: rgba('#516F77', 0.1) },
  },
}

export const midnightTheme: Theme = {
  primary: colors.navy,
  secondary: colors.darkNavy,
  accent: colors.green,
  action: colors.darkNavy,
  error: colors.error,
  boxShadow: SHADOW_SM,
  highlight: rgba('#FFF', 0.05),
  gray: rgba('#FFF', 0.5),
  white: '#FFF',
  black: '#000',
  radius: RADIUS_SM,
  tertiary: colors.blueMed,
  chart: colors.blueLight,
  header: '#191C26',
  title: colors.white,
  beforeSelected: '#9CA6AD',
  afterSelected: rgba('#FFF', 0.5),
  panelBack: colors.navy,
  secondaryPanel: rgba('#000000', 0.23),
  subRow: colors.darkNavy,
  actionsDrop: { default: colors.navy, hover: '#303444' },
  tableRow: rgba('#FFF', 0.05),
  invertedButton: colors.darkNavy,
  label: colors.white,
  inputBorder: colors.white,
  selectorBack: colors.tableHighlight,
  breadcrumb: rgba('#FFF', 0.5),
  progressBack: colors.progressBack,
  grayDivider: colors.grayDivider,
  actionInfo: colors.actionInfo,
  oddRow: rgba('#FFF', 0.05),
  scrollBar: colors.blueMed,
  disabled: colors.fadedGrey,
  welcomeBack: '#2B3249',
  whiteIcon: colors.white,
  boxHover: rgba(colors.black, 0.4),
  listText: colors.white,
  listDesc: '#9CA6AD',
  listBack: '#2B3249',
  listCard: colors.darkNavy,
  listCompleted: '#2B3249',
  listIcon: colors.white,
  listBorder: rgba('#CEDAE1', 0.3),
  whiteGlove: colors.navy,
  buttonHover: '#303444',
  dropHover: rgba('#0D1017', 0.35),
  buttonActive: rgba('#2C3040', 0.6),
  checkbox: '#19191D',
  warning: '#F5A623',
  newNav: '#313540',
  tableHeader: '#161B2A',
  rowHighlight: '#283840',
  inputBack: '#373B4A',
  buttonBack: colors.navy,
  warningInverted: '#19191D',
  input: { default: '#373B4A', basic: '#19191D', disabled: rgba('#516F77', 0.1) },
  dock: { default: '#394E5B', hover: '#19191D', disabled: rgba('#516F77', 0.1) },
  tab: { default: '#394E5B', hover: '#303444', disabled: rgba('#516F77', 0.1) },
  drop: { options: '#191C26', back: '#19191D', text: '', selected: '#2D303C' },
  profileDrop: '#30424D',
  ellipseOption: colors.navy,
  modalBack: '#2B3249',
  modalOptionsBack: colors.darkNavy,
  chartBack: '#191C26',
  walletBack: rgba('#000000', 0.23),
  walletCat: '#2B3249',
  button: {
    primary: { default: colors.green, hover: COLOR_BRAND_PRIMARY_HOVER, submitting: COLOR_BRAND_PRIMARY_ACTIVE, disabled: rgba('#33BB40', 0.32) },
    inverted: { default: '#19191D', hover: '#303444', submitting: COLOR_BASE_NAVY, hoverColor: COLOR_BRAND_PRIMARY_HOVER, disabled: rgba(81, 111, 119, 0.101961) },
    cancel: { default: COLOR_BASE_NAVY, hover: '#303444', submitting: COLOR_BASE_NAVY, disabled: rgba('#516F77', 0.1) },
    error: { default: colors.errorRed, hover: '#CC4545', disabled: rgba('#516F77', 0.1) },
    warning: { default: COLOR_SEMANTIC_WARNING, hover: '#F17C15', submitting: '#C35B00', text: '#262626', disabled: rgba('#516F77', 0.1) },
    warningI: { default: '#19191D', hover: '#303444', submitting: COLOR_BASE_NAVY, text: '#262626', disabled: rgba('#516F77', 0.1) },
  }
}

export const lightTheme: Theme = {
  primary: rgba('#f2f2f2', 1),
  secondary: rgba('#f2f2f2', 1),
  accent: colors.green,
  action: rgba('#394E5B', 0.1),
  error: colors.error,
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
  highlight: rgba('#394E5B', 0.5),
  gray: rgba('#FFF', 0.5),
  white: '#262626',
  black: colors.black,
  radius: RADIUS_SM,
  tertiary: colors.white,
  chart: colors.blueLight,
  header: '#FAFAFA',
  text: colors.darkNavy,
  title: colors.lightNavy,
  afterSelected: rgba('#394E5B', 0.5),
  beforeSelected: '#9CA6AD',
  panelBack: '#FAFAFA',
  secondaryPanel: '#CEDAE1',
  subRow: rgba('#000000', 0.05),
  actionsDrop: { default: '#F2F2F2', hover: '#92AABA' },
  tableRow: colors.white,
  invertedButton: colors.white,
  label: '#262626',
  inputBorder: colors.dropBorder,
  selectorBack: rgba('#394E5B', 0.1),
  breadcrumb: colors.lightNavy,
  progressBack: colors.lightNavy,
  grayDivider: rgba('#394E5B', 0.1),
  actionInfo: colors.white,
  oddRow: rgba('#000000', 0.1),
  scrollBar: rgba('#000000', 0.2),
  disabled: rgba(colors.darkNavy, 0.6),
  welcomeBack: colors.white,
  whiteIcon: '#9CA6AD',
  boxHover: rgba('#30424D', 0.3),
  listText: '#262626',
  listDesc: '#9CA6AD',
  listBack: '#F9F9FA',
  listCard: '#F2F2F2',
  listCompleted: '#F9F9FA',
  listIcon: '#9CA6AD',
  listBorder: rgba('#CEDAE1', 0.3),
  whiteGlove: '#638599',
  buttonHover: '#92AABA',
  dropHover: '#CEDAE1',
  buttonActive: rgba('#B0C3D0', 0.6),
  checkbox: '#B4B4BB',
  warning: '#F5A623',
  newNav: colors.white,
  tableHeader: '#F7F9FA',
  rowHighlight: 'linear-gradient(0deg, rgba(51, 187, 64, 0.3216), rgba(51, 187, 64, 0.3216)), #FFFFFF',
  inputBack: colors.white,
  buttonBack: '#F2F2F2',
  warningInverted: '#FAFAFA',
  input: { default: '#FFFFFF', basic: '#FFFFFF', disabled: rgba('#FFFFFF', 0.1) },
  dock: { default: '#394E5B', hover: 'transparent', disabled: rgba('#516F77', 0.1) },
  tab: { default: '#394E5B', hover: '#F0F0F1', disabled: rgba('#516F77', 0.1) },
  drop: { options: '#FAFAFA', back: '#FFFFFF', text: '#FFFFFF', selected: '#CEDAE1' },
  profileDrop: '#30424D',
  ellipseOption: '#FAFAFA',
  modalBack: '#F5F5F5',
  modalOptionsBack: '#FBFBFB',
  chartBack: colors.white,
  walletBack: '#FBFBFB',
  walletCat: '#F0F0F1',
  button: {
    primary: { default: colors.green, hover: COLOR_BRAND_PRIMARY_HOVER, submitting: COLOR_BRAND_PRIMARY_ACTIVE, disabled: rgba('#33BB40', 0.32) },
    inverted: { default: '#FAFAFA', hover: '#FAFAFA', submitting: '#B0C3D0', hoverColor: COLOR_BRAND_PRIMARY_HOVER, disabled: rgba(81, 111, 119, 0.101961) },
    cancel: { default: rgba('#638599', 0), hover: '#92AABA', submitting: '#B0C3D0', disabled: rgba('#516F77', 0.1) },
    error: { default: colors.errorRed, hover: '#CC4545', disabled: rgba('#516F77', 0.1) },
    warning: { default: '#F5A623', hover: '#F17C15', submitting: '#C35B00', text: '#262626', disabled: rgba('#516F77', 0.1) },
    warningI: { default: '#FAFAFA', hover: '#FAFAFA', submitting: '#B0C3D0', disabled: rgba('#516F77', 0.1) },
  }
}

export enum Themes {
  EQUA,
  MIDNIGHT,
  LIGHT,
}

export const getTheme = (theme: Themes) => {
  switch (theme) {
    case Themes.EQUA:
      return equaTheme
    case Themes.MIDNIGHT:
      return midnightTheme
    case Themes.LIGHT:
      return lightTheme
    default:
      return equaTheme
  }
}
